import Vue from "vue";
import {
  DOCUMENT_STATUS,
  IDENTIFICATION_LOCAL_STATUS,
  IDENTIFICATION_REGISTRY_STATUS,
  WORK_STATUS
} from "@/constants";

function resetIdentificationState(state) {
  state.identificationId = null;
  state.identifications = {};
}

function pruneIdentifications(state, activeIdentificationId) {
  Object.keys(state.identifications).forEach(identificationId => {
    if (identificationId !== activeIdentificationId) {
      Vue.delete(state.identifications, identificationId);
    }
  });
}

function getIdentificationEntry(state, identificationId) {
  return state.identifications[identificationId] || null;
}

export default {
  toggleOnline(state, isOnline) {
    state.isOnline = isOnline;
  },
  workStarting(state) {
    state.status = WORK_STATUS.STARTING;
    state.error = null;
    resetIdentificationState(state);
  },
  workStartError(state, error) {
    state.status = WORK_STATUS.START_ERROR;
    state.error = error;
  },
  workStarted(state) {
    state.status = WORK_STATUS.WORKING;
  },
  workReconnecting(state) {
    if (state.status !== WORK_STATUS.WORKING) return;

    state.status = WORK_STATUS.RECONNECTING;
  },
  workReconnected(state) {
    if (state.status !== WORK_STATUS.RECONNECTING) return;

    state.status = WORK_STATUS.WORKING;
  },
  workStopping(state) {
    state.status = WORK_STATUS.STOPPING;
  },
  workStopError(state, error) {
    state.status = WORK_STATUS.STOP_ERROR;
    state.error = error;
  },
  workStopped(state, error) {
    state.status = WORK_STATUS.STOPPED;
    state.error = error;
    resetIdentificationState(state);
  },
  workSetIdentification(state, { identificationId, lastActionUtc }) {
    state.identificationId = identificationId;
    pruneIdentifications(state, identificationId);
    const identification = getIdentificationEntry(state, identificationId);
    if (identification) {
      identification.lastActionUtc = lastActionUtc;
    }
  },

  initIdentification(
    state,
    { identificationId, id, ...initialIdentification }
  ) {
    pruneIdentifications(state, identificationId);
    Vue.set(state.identifications, identificationId, {
      ...initialIdentification,
      id,
      identificationId,
      status: IDENTIFICATION_LOCAL_STATUS.INITIAL,
      loaded: false,
      error: null,
      data: {
        document: {
          status: DOCUMENT_STATUS.INITIAL,
          error: null,
          data: null,
          validations: null
        },
        status: null,
        registration: null,
        media: null,
        rules: null,
        fraudChecks: null,
        registries: {
          status: IDENTIFICATION_REGISTRY_STATUS.INITIAL,
          error: null,
          data: null
        },
        scores: null,
        procedures: null,
        proofOfAddress: null,
        companyName: null
      }
    });
  },

  identificationRegistryReprocessInit(state, { identificationId }) {
    const identification = getIdentificationEntry(state, identificationId);
    if (!identification) return;

    identification.data.registries.status =
      IDENTIFICATION_REGISTRY_STATUS.REPROCESSING;
  },
  identificationRegistryReprocessError(state, { identificationId, error }) {
    const identification = getIdentificationEntry(state, identificationId);
    if (!identification) return;

    const identificationRegistries = identification.data.registries;
    identificationRegistries.status =
      IDENTIFICATION_REGISTRY_STATUS.REPROCESS_ERROR;
    identificationRegistries.error = error;
  },
  identificationRegistryReprocessSuccess(state, { identificationId, data }) {
    const identification = getIdentificationEntry(state, identificationId);
    if (!identification) return;

    const identificationData = identification.data;

    identificationData.registries.data = data.registries;
    identificationData.registries.status =
      IDENTIFICATION_REGISTRY_STATUS.REPROCESS_SUCCESS;

    identificationData.registries.fraudChecks = data.fraudChecks;
  },

  getIdentificationInit(state, { identificationId }) {
    const identification = getIdentificationEntry(state, identificationId);
    if (!identification) return;

    identification.status = IDENTIFICATION_LOCAL_STATUS.LOADING;
  },
  getIdentificationSuccess(
    state,
    {
      identificationId,
      data: {
        document,
        documentValidations,
        registration,
        media,
        rules,
        registries,
        fraudChecks,
        scores,
        status,
        companyName,
        procedures,
        proofOfAddress
      }
    }
  ) {
    const identification = getIdentificationEntry(state, identificationId);
    if (!identification) return;

    identification.loaded = true;
    identification.status = IDENTIFICATION_LOCAL_STATUS.CHECKING;
    identification.data.status = status;
    identification.data.companyName = companyName;
    identification.data.registration = registration;
    identification.data.media = media;
    identification.data.rules = rules;
    identification.data.fraudChecks = fraudChecks;
    identification.data.registries.data = registries;
    identification.data.scores = scores;
    identification.data.document.data = document;
    identification.data.document.validations = documentValidations;
    identification.data.procedures = procedures;
    identification.data.proofOfAddress = proofOfAddress;
  },
  getIdentificationError(state, { identificationId, error }) {
    const identification = getIdentificationEntry(state, identificationId);
    if (!identification) return;

    identification.status = IDENTIFICATION_LOCAL_STATUS.LOAD_ERROR;
    identification.loaded = false;
    identification.error = error;
    state.identificationId = null;
  },

  putIdentificationDocumentInit(state, { identificationId }) {
    const identification = getIdentificationEntry(state, identificationId);
    if (!identification) return;

    identification.data.document.status = DOCUMENT_STATUS.UPDATING;
  },
  putIdentificationDocumentSuccess(state, { identificationId, document }) {
    const identification = getIdentificationEntry(state, identificationId);
    if (!identification) return;

    identification.data.document.status = DOCUMENT_STATUS.UPDATED;
    identification.data.document.data = document;
  },
  putIdentificationDocumentError(state, { identificationId, error }) {
    const identification = getIdentificationEntry(state, identificationId);
    if (!identification) return;

    identification.data.document.status = DOCUMENT_STATUS.UPDATE_ERROR;
    identification.data.document.error = error.message;
  },

  invokeIdentificationRerouteSuccess(state) {
    state.identificationId = null;
  },

  putIdentificationApproveInit(state, { identificationId }) {
    const identification = getIdentificationEntry(state, identificationId);
    if (!identification) return;

    identification.status = IDENTIFICATION_LOCAL_STATUS.APPROVING;
  },
  putIdentificationApproveSuccess(state, { identificationId }) {
    const identification = getIdentificationEntry(state, identificationId);
    if (!identification) return;

    identification.status = IDENTIFICATION_LOCAL_STATUS.APPROVED;
    state.identificationId = null;
  },
  putIdentificationApproveError(state, { identificationId, error }) {
    const identification = getIdentificationEntry(state, identificationId);
    if (!identification) return;

    identification.status = IDENTIFICATION_LOCAL_STATUS.APPROVE_ERROR;
    identification.error = error.message;
  },

  putIdentificationRejectInit(state, { identificationId }) {
    const identification = getIdentificationEntry(state, identificationId);
    if (!identification) return;

    identification.status = IDENTIFICATION_LOCAL_STATUS.REJECTING;
  },
  putIdentificationRejectSuccess(state, { identificationId }) {
    const identification = getIdentificationEntry(state, identificationId);
    if (!identification) return;

    identification.status = IDENTIFICATION_LOCAL_STATUS.REJECTED;
    state.identificationId = null;
  },
  putIdentificationRejectError(state, { identificationId, error }) {
    const identification = getIdentificationEntry(state, identificationId);
    if (!identification) return;

    identification.status = IDENTIFICATION_LOCAL_STATUS.REJECT_ERROR;
    identification.error = error.message;
  },
  changeDocumentEditStatus(state, editEnabled) {
    state.isDocumentEditEnabled = editEnabled;
  },
  changePOAEditStatus(state, editEnabled) {
    state.isPOAEditEnabled = editEnabled;
  },

  putIdentificationPOAInit(state, { identificationId }) {
    const identification = getIdentificationEntry(state, identificationId);
    if (!identification?.data.proofOfAddress) return;

    Vue.set(
      identification.data.proofOfAddress,
      "status",
      DOCUMENT_STATUS.UPDATING
    );
  },
  putIdentificationPOASuccess(state, { identificationId, poa }) {
    const identification = getIdentificationEntry(state, identificationId);
    if (!identification?.data.proofOfAddress) return;

    const updatedValues = {
      ...identification.data.proofOfAddress,
      status: DOCUMENT_STATUS.UPDATED
    };
    Object.keys(poa).forEach(key => {
      updatedValues[key] = { ...updatedValues[key], value: poa[key] };
    });
    state.isPOAEditEnabled = false;
    identification.data.proofOfAddress = updatedValues;
  },
  putIdentificationPOAError(state, { identificationId, error }) {
    const identification = getIdentificationEntry(state, identificationId);
    if (!identification?.data.proofOfAddress) return;

    identification.data.proofOfAddress.status = DOCUMENT_STATUS.UPDATE_ERROR;
    identification.data.proofOfAddress.error = error.message;
  }
};
